import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { PACKAGE_CONDITION } from '@returnmates/client-core/src/constants/trip'
import {
  Package,
  PackageStatus,
  Partner,
  PartnerDestination,
  Retailer,
} from '@returnmates/client-core/src/graphql/generated/api'
import {
  getCarriers,
  getConsolidationByIdSelector,
  getMainRole,
  getPackageServiceTypes,
  getRetailers,
} from '@returnmates/client-core/src/selectors/admin'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import AutocompleteInput from '@returnmates/ui-core/src/components/AutocompleteInput'
import Button from '@returnmates/ui-core/src/components/Button'
import Input from '@returnmates/ui-core/src/components/Input'
import useOutsideClick from '@returnmates/ui-core/src/hooks/useOutsideClick'
import clsx from 'clsx'
import { HTMLAttributes, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Field, useField } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import { cognitoGroups } from '../../../constants/userPermissions'
import useEnabledPartners from '../../../hooks/useEnabledPartners'
import NewAddressDropdown from '../../../pages/Consolidations/components/NewAddressDropdown'
import PaperComponentCustom from '../../../pages/Consolidations/components/PaperComponentCustom'
import { NOT_AVAILABLE_PACKAGE_STATUSES_TO_ADD_TO_CONSOLIDATION } from '../../../pages/Packages/constants'
import PlusIcon from '../../images/icons/plus'
import Select from '../../Select'
import WrappedSelect from '../../WrappedSelect'
import useStyles from '../styles'

interface Props {
  currentPackage: Package | null
}

const CarrierInformation = ({ currentPackage }: Props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const packageConsolidation = useSelector(getConsolidationByIdSelector)
  const carriers = useSelector(getCarriers)
  const retailers = useSelector(getRetailers)
  const userRole = useSelector(getMainRole)
  const packageServiceTypes = useSelector(getPackageServiceTypes)
  const partners = useEnabledPartners()
  const { input: consolidationIdInput } = useField('consolidationId')
  const { input: carrierInput } = useField('carrier')
  const { input: retailerInput } = useField('retailer')
  const { input: partnerInput } = useField('partner')
  const { input: partnerDestinationIdInput } = useField('returnDestinationId')
  const [isRetailerModalOpen, setIsRetailerModalOpen] = useState(false)
  const [retailerError, setRetailerError] = useState('')
  const [isAddNewRetailerDisabled, setIsAddNewRetailerDisabled] = useState(false)
  const [newRetailer, setNewRetailer] = useState('')
  const [partnerDestinationValue, setPartnerDestinationValue] = useState<PartnerDestination | null>(
    null,
  )
  const [
    partnerDestinations,
    setPartnerDestinations,
  ] = useState<Array<PartnerDestination | null> | null>([])
  const [isDestinationOpen, setIsDestinationOpen] = useState(false)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const retailerModalRef = useRef<HTMLDivElement>(null)

  const onChangeCarrier = useCallback(
    val => {
      carrierInput.onChange(val)
    },
    [carrierInput],
  )

  const onChangeRetailer = useCallback(
    val => {
      const isRetailerAlreadyExist = retailers?.some(
        retailer => retailer.name?.toLocaleLowerCase() === val.toLocaleLowerCase(),
      )

      if (isRetailerAlreadyExist) {
        setIsAddNewRetailerDisabled(true)
        setRetailerError(`${val} already exist`)
      } else {
        setIsAddNewRetailerDisabled(false)
        setRetailerError('')
      }

      setNewRetailer(val)
    },
    [retailers],
  )

  const toggleRetailerModal = useCallback(
    () => setIsRetailerModalOpen(prevIsRetailerModalOpen => !prevIsRetailerModalOpen),
    [],
  )

  const onCloseRetailerModal = useCallback(() => setIsRetailerModalOpen(false), [])

  useOutsideClick(retailerModalRef, onCloseRetailerModal)

  const onSelectRetailer = useCallback(
    (_, value) => {
      retailerInput.onChange(value)
    },
    [retailerInput],
  )

  const addRetailer = useCallback(async () => {
    try {
      const createdRetailer = await createAsyncAction(
        dispatch,
        actions.createRetailer.request({ name: newRetailer }),
      )

      retailerInput.onChange(createdRetailer)

      setNewRetailer('')
      setIsRetailerModalOpen(false)
    } catch (err) {
      const { message } = err as Error
      setRetailerError(message || (err as string))
    }
  }, [dispatch, newRetailer, retailerInput])

  const onSelectPartnerDestination = useCallback(
    (_, value) => {
      if (value) {
        partnerDestinationIdInput.onChange(value?.address?.id)
        setPartnerDestinationValue(value)
        setIsDestinationOpen(false)
      } else {
        partnerDestinationIdInput.onChange('')
        setPartnerDestinationValue(null)
      }
    },
    [partnerDestinationIdInput],
  )

  const handlePartnerDestinationOpen = useCallback(() => {
    setIsDestinationOpen(true)
  }, [])

  const handlePartnerDestinationClose = useCallback(() => {
    if (!isAddressModalOpen) {
      setIsDestinationOpen(false)
      setIsAddressModalOpen(false)
    }
  }, [isAddressModalOpen])

  const handleCloseAddressDropdown = useCallback(() => {
    setIsDestinationOpen(false)
    setIsAddressModalOpen(false)
  }, [])

  const handleOpenAddressModal = useCallback(() => {
    setIsAddressModalOpen(true)
    setIsDestinationOpen(true)
  }, [])

  const isAddRetailerAvailable = useMemo(
    () =>
      userRole === cognitoGroups.admin ||
      userRole === cognitoGroups.manager ||
      userRole === cognitoGroups.operations,
    [userRole],
  )

  const serviceTypeCondition = useMemo(() => {
    return packageServiceTypes?.reduce((acc, item: { id: string }) => {
      const formattedValue = item?.id.includes('NEXTDAY')
        ? item?.id.replace('NEXTDAY', 'Next Day ')
        : item?.id.replace('SAMEDAY_', 'Same Day ')

      acc = { ...acc, [item.id]: formattedValue }

      return acc
    }, {} as { [key: string]: string })
  }, [packageServiceTypes])

  const getConsolidationById = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        actions.getConsolidationById.request({
          where: {
            consolidationId: consolidationIdInput.value,
          },
        }),
      )
    } catch (err) {
      const { message } = err as Error
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: errorMapper(message || (err as string)),
        }),
      )
    }
  }, [consolidationIdInput.value, dispatch])

  useEffect(() => {
    if (!retailerInput) {
      setIsAddNewRetailerDisabled(true)
    }
  }, [retailerInput])

  useEffect(() => {
    if (partners && partners.length) {
      const destinations = partners.find((p: Partner) => p.code === partnerInput?.value?.code)
        ?.destinations

      setPartnerDestinations(destinations || null)
      setPartnerDestinationValue(
        destinations?.find(d => d?.address?.id === partnerDestinationIdInput.value) || null,
      )
    }
  }, [partnerDestinationIdInput, partners, partnerInput])

  useEffect(() => {
    if (consolidationIdInput.value && packageConsolidation?.id !== consolidationIdInput.value) {
      getConsolidationById()
    }
  }, [
    consolidationIdInput.value,
    consolidationIdInput.values,
    getConsolidationById,
    packageConsolidation?.id,
  ])

  return (
    <>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Carrier</p>
        <Field name="carrier">
          {({ input }) => (
            <WrappedSelect
              value={
                (consolidationIdInput.value && packageConsolidation?.carrier) ||
                input.value ||
                currentPackage?.carrier
              }
              onChange={onChangeCarrier}
              disabled={!!consolidationIdInput.value}
              values={carriers || []}
              objectKey="id"
              placeholder={consolidationIdInput.value ? '' : 'Choose carrier'}
              popupIconStyles={consolidationIdInput.value ? classes.hidePopupIcon : ''}
              fullWidth
            />
          )}
        </Field>
      </div>

      <div className={clsx(classes.detailsRow, classes.carrierDetailsRow)}>
        <div className={classes.inputHeaderWrap}>
          <p className={classes.inputHead}>Retailer</p>
          {isAddRetailerAvailable ? (
            <div className={classes.addButton} onClick={toggleRetailerModal}>
              <PlusIcon /> Add new
            </div>
          ) : null}
        </div>

        {isRetailerModalOpen && (
          <div ref={retailerModalRef} className={classes.newRetailerModal}>
            <p className={classes.newRetailerModalTitle}>Add new retailer</p>
            <Input
              placeholder="Retailer name"
              classes={{
                textField: classes.input,
              }}
              onChange={onChangeRetailer}
              value={newRetailer}
              fullWidth
              error={Boolean(retailerError)}
              helperText={retailerError}
            />
            <div className={clsx(classes.modalButtons, { [classes.errorSpace]: retailerError })}>
              <Button
                className={clsx(classes.button, classes.cancelButton)}
                onClick={onCloseRetailerModal}
                label="Cancel"
                cancelButton
              />
              <Button
                className={classes.button}
                onClick={addRetailer}
                label="Save"
                disabled={isAddNewRetailerDisabled}
              />
            </div>
          </div>
        )}

        <Field name="retailer">
          {({ input, meta }) => (
            <AutocompleteInput
              options={retailers || []}
              value={input.value || null}
              onChange={onSelectRetailer}
              isOptionEqualToValue={(option: Retailer, value: Retailer) => option.id === value.id}
              getOptionLabel={(option: Retailer) => option.name || ''}
              renderOption={(events: HTMLAttributes<HTMLElement>, option: Retailer) => (
                <p className={classes.optionPrimary} {...events} key={option.name}>
                  {option.name}
                </p>
              )}
              placeholder="Choose retailer"
              inputRootStyles={classes.inputRoot}
              meta={meta}
            />
          )}
        </Field>
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Condition</p>
        <Field name="condition">
          {({ input }) => (
            <Select
              value={input.value}
              onChange={input.onChange}
              values={PACKAGE_CONDITION}
              className={classes.selectField}
              fullWidth
              wrapperClassName={classes.selectWrapper}
            />
          )}
        </Field>
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Return Destination (string)</p>
        <Field name="returnDestination">
          {({ input, meta }) => (
            <Input
              placeholder="Add return destination (string)"
              classes={{
                textField: classes.input,
              }}
              onChange={input.onChange}
              value={input.value}
              fullWidth
              helperText={meta.error}
              error={meta.touched && meta.error}
            />
          )}
        </Field>
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Return Destination</p>
        <Field name="returnDestinationId">
          {({ meta }) => (
            <AutocompleteInput
              options={partnerDestinations || []}
              onChange={onSelectPartnerDestination}
              readOnly={NOT_AVAILABLE_PACKAGE_STATUSES_TO_ADD_TO_CONSOLIDATION?.includes(
                currentPackage?.status as PackageStatus,
              )}
              inputRootStyles={classes.inputRoot}
              onOpen={handlePartnerDestinationOpen}
              onClose={handlePartnerDestinationClose}
              getOptionLabel={(option: PartnerDestination) =>
                `${option.address?.street}, ${option.address?.city}, ${option.address?.state}, ${option.address?.zipCode}`
              }
              renderOption={(events: HTMLAttributes<HTMLElement>, option: PartnerDestination) => (
                <p
                  {...events}
                  key={option.id}
                  className={`${events.className} ${classes.optionSecondary}`}
                >
                  {option.address?.street}, {option.address?.city}, {option.address?.state},{' '}
                  {option.address?.zipCode}
                </p>
              )}
              inputValue={partnerDestinationValue || ''}
              value={partnerDestinationValue || null}
              placeholder="Select partner destination..."
              noOptionsText={
                partnerInput.value
                  ? 'The Selected Partner Has No Return Destinations'
                  : 'Select a Partner to Populate Destinations'
              }
              meta={meta}
              isOpen={isDestinationOpen}
              PaperComponent={options =>
                isAddressModalOpen ? (
                  <NewAddressDropdown
                    className={options.className}
                    onCloseDropdown={handleCloseAddressDropdown}
                    partnerCode={partnerInput.value?.code}
                    setPartnerDestinationValue={setPartnerDestinationValue}
                  />
                ) : (
                  <PaperComponentCustom
                    className={options.className}
                    children={options.children}
                    showAddButton={Boolean(partnerInput.value)}
                    onButtonClick={handleOpenAddressModal}
                  />
                )
              }
            />
          )}
        </Field>
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Dimensions (LxWxH) (in.)</p>
        <div className={classes.dimensionsContainer}>
          <Field name="length">
            {({ input, meta }) => (
              <Input
                placeholder="Length"
                classes={{
                  textField: classes.input,
                }}
                onChange={input.onChange}
                value={input.value}
                type="number"
                helperText={meta.error}
                error={meta.touched && meta.error}
              />
            )}
          </Field>
          <Field name="width">
            {({ input, meta }) => (
              <Input
                placeholder="Width"
                classes={{
                  textField: classes.input,
                }}
                onChange={input.onChange}
                value={input.value}
                type="number"
                helperText={meta.error}
                error={meta.touched && meta.error}
              />
            )}
          </Field>
          <Field name="height">
            {({ input, meta }) => (
              <Input
                placeholder="Height"
                classes={{
                  textField: classes.input,
                }}
                onChange={input.onChange}
                value={input.value}
                type="number"
                helperText={meta.error}
                error={meta.touched && meta.error}
              />
            )}
          </Field>
        </div>
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Package Weight (oz.)</p>
        <Field name="weight">
          {({ input, meta }) => (
            <Input
              placeholder="Weight"
              classes={{
                textField: classes.input,
              }}
              onChange={input.onChange}
              value={input.value}
              type="number"
              fullWidth
              helperText={meta.error}
              error={meta.touched && meta.error}
            />
          )}
        </Field>
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Service Type</p>
        <Field name="serviceType">
          {({ input }) => (
            <Select
              value={input.value}
              onChange={input.onChange}
              values={serviceTypeCondition || {}}
              disabled={userRole !== cognitoGroups.admin}
              className={classes.selectField}
              fullWidth
              wrapperClassName={classes.selectWrapper}
            />
          )}
        </Field>
      </div>
    </>
  )
}

export default memo(CarrierInformation)
